import {
  fitWithin,
  PHOTO_MIME_TYPE,
  type PixelSize,
  STORED_JPEG_QUALITY,
  STORED_MAX_EDGE_PX,
  THUMBNAIL_JPEG_QUALITY,
  THUMBNAIL_MAX_EDGE_PX,
} from '~/calculations/imageScaling'
import type { Result } from '~/utils/result'
import { err, ok } from '~/utils/result'

/**
 * The capture pipeline (D-081).
 *
 * ────────────────────────────────────────────────────────────────────────────
 *  THIS FILE CARRIES THE PRIVACY GUARANTEE. IF IT STOPS RE-ENCODING, EVERY
 *  PHOTO IN THE DATABASE BEGINS CARRYING THE USER'S HOME ADDRESS.
 * ────────────────────────────────────────────────────────────────────────────
 *
 * Every image entering the app is drawn to a canvas and re-encoded, and the
 * camera's own bytes are then dropped. One operation answers three separate
 * problems, which is why it is a single unconditional step rather than a set of
 * options:
 *
 *  1. **Orientation is baked into the pixels.** `createImageBitmap` is asked to
 *     apply EXIF orientation, so the canvas receives the image the right way up
 *     and no downstream renderer has to honour a rotation flag — or fail to.
 *  2. **All metadata is dropped.** A canvas re-encode carries no EXIF forward:
 *     no GPS fix, no device, no camera timestamp. A progress photo taken at
 *     home otherwise embeds the user's home address in every backup.
 *  3. **HEIC becomes JPEG.** iOS hands out HEIC, which many readers of an
 *     exported media ZIP cannot open.
 *
 * **There is deliberately no "keep the original" path.** The guarantee is meant
 * to be structural: no blob in this database has ever held a location, so no
 * later export bug can leak one.
 *
 * Nothing here crops, stretches, smooths, retouches or corrects the image.
 * `ui-ux-spec.md` forbids reshaping or beautifying the body, and a resize that
 * changed the aspect ratio would do exactly that while looking like rounding.
 */

export type PhotoPipelineErrorKind =
  /** The file is not an image this browser can decode. */
  | 'unreadableImage'
  /** The browser gave us no 2D canvas context — nothing can be re-encoded. */
  | 'canvasUnavailable'
  /** Encoding produced no bytes. */
  | 'encodeFailed'

export interface PhotoPipelineError {
  readonly kind: PhotoPipelineErrorKind
  readonly message: string
}

export interface ProcessedPhoto {
  readonly image: Blob
  readonly thumbnail: Blob
  readonly size: PixelSize
}

function pipelineError(kind: PhotoPipelineErrorKind, message: string): PhotoPipelineError {
  return { kind, message }
}

/**
 * Whether this browser can run the pipeline at all.
 *
 * Feature-detected rather than assumed, and checked before the camera opens:
 * discovering it after the shutter would lose a photo that cannot be retaken.
 */
export function isPipelineSupported(): boolean {
  return (
    typeof createImageBitmap === 'function' &&
    typeof document !== 'undefined' &&
    typeof HTMLCanvasElement !== 'undefined'
  )
}

/**
 * Re-encode a captured file into a stored image and its thumbnail.
 *
 * The bitmap is closed in a `finally` so a decode failure does not leak the
 * decoded frame — on a phone that is several megabytes per capture, and the
 * grid is already the memory-sensitive part of this screen.
 */
export async function processCapturedImage(
  file: Blob,
): Promise<Result<ProcessedPhoto, PhotoPipelineError>> {
  let bitmap: ImageBitmap
  try {
    /* `imageOrientation: 'from-image'` is what applies the EXIF rotation. It is
       the reason a portrait iPhone photo does not arrive on its side. */
    bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  } catch (cause) {
    return err(
      pipelineError(
        'unreadableImage',
        cause instanceof Error && cause.message !== ''
          ? `That image could not be read: ${cause.message}`
          : 'That image could not be read.',
      ),
    )
  }

  try {
    const source: PixelSize = { width: bitmap.width, height: bitmap.height }
    if (!(source.width > 0) || !(source.height > 0)) {
      return err(pipelineError('unreadableImage', 'That image has no width or height.'))
    }

    const image = await drawAndEncode(
      bitmap,
      fitWithin(source, STORED_MAX_EDGE_PX),
      STORED_JPEG_QUALITY,
    )
    if (!image.ok) return image

    const thumbnail = await drawAndEncode(
      bitmap,
      fitWithin(source, THUMBNAIL_MAX_EDGE_PX),
      THUMBNAIL_JPEG_QUALITY,
    )
    if (!thumbnail.ok) return thumbnail

    return ok({
      image: image.value.blob,
      thumbnail: thumbnail.value.blob,
      size: image.value.size,
    })
  } finally {
    bitmap.close()
  }
}

async function drawAndEncode(
  bitmap: ImageBitmap,
  size: PixelSize,
  quality: number,
): Promise<Result<{ blob: Blob; size: PixelSize }, PhotoPipelineError>> {
  const canvas = document.createElement('canvas')
  canvas.width = size.width
  canvas.height = size.height

  const context = canvas.getContext('2d')
  if (context === null) {
    return err(
      pipelineError('canvasUnavailable', 'This browser would not provide a drawing surface.'),
    )
  }

  /* A JPEG has no alpha channel, so anything transparent in the source would
     encode as black. Painting white first keeps a transparent PNG readable. */
  context.fillStyle = '#ffffff'
  context.fillRect(0, 0, size.width, size.height)
  context.imageSmoothingQuality = 'high'
  context.drawImage(bitmap, 0, 0, size.width, size.height)

  const blob = await canvasToBlob(canvas, quality)
  /* Collapse the canvas immediately. Safari holds the backing store — several
     megabytes at 1440 px — until the element is collected, and a capture
     session creates two of these per photo. */
  canvas.width = 0
  canvas.height = 0

  if (blob === undefined || blob.size === 0) {
    return err(pipelineError('encodeFailed', 'The photo could not be saved in this browser.'))
  }
  return ok({ blob, size })
}

function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob | undefined> {
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob ?? undefined), PHOTO_MIME_TYPE, quality)
  })
}
